  <template>
    <div class="surat-keluar-view">
      <!-- Loading Overlay -->
      <div v-if="pageLoading" class="loading-overlay">
        <a-spin size="large" />
      </div>

      <template v-else-if="suratData && suratData.nomorSurat">
        <!-- Page Header -->
        <div class="page-header">
          <h2 class="page-title">Detail Surat Keluar</h2>
          <p class="page-subtitle">{{ suratData.nomorSurat }}</p>
          <a-tag :style="getStatusStyle(suratData.status)" class="status-tag">
            <strong>{{ suratData.status === 'Revisi' ? 'Revisi Verifikator' : suratData.status }}</strong>
          </a-tag>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <!-- Download Surat Button -->
          <a-button
            class="btn-pill-red-outline"
            @click="handleDownloadSurat"
          >
            <template #icon>
              <DownloadOutlined />
            </template>
            DOWNLOAD SURAT
          </a-button>

          <!-- Batalkan Surat Button (only for owner with Belum Dikirim status) -->
          <a-button
            v-if="canCancel"
            type="default"
            class="btn-pill-black"
            @click="handleCancelSurat"
            :loading="cancelLoading"
          >
            <template #icon>
              <StopOutlined />
            </template>
            BATALKAN SURAT
          </a-button>

          <!-- Duplicate Button - All sekretaris and super_admin can duplicate -->
          <a-button
            v-if="canDuplicateSurat"
            type="default"
            class="btn-pill-black"
            @click="handleDuplicateSurat"
          >
            <template #icon>
              <CopyOutlined />
            </template>
            BUAT SURAT BARU DARI SURAT INI
          </a-button>
        </div>

        <!-- Detail Pembatalan Section (only show for Dibatalkan status) -->
        <DetailPembatalan
          :alasan-pembatalan="suratData.alasanPembatalan"
          :status="suratData.status"
        />

        <!-- Info Card - Remove dividers between fields -->
        <div class="info-card">
          <div class="info-row">
            <span class="info-label">Pengaju Surat</span>
            <span class="info-value">{{ suratData.creator?.name || '-' }} ({{ suratData.creator?.jabatan || 'Staf Sekretariat' }})</span>
          </div>
          <div class="info-row">
            <span class="info-label">Waktu Dibuat/Unggah</span>
            <span class="info-value">{{ formatDateTime(suratData.createdAt) }} (Terakhir diperbarui {{ formatDateTime(suratData.updatedAt) }})</span>
          </div>
          <!-- Waktu Ditandatangani - Show for Belum Dikirim and Terkirim status -->
          <div class="info-row" v-if="suratData.status === 'Belum Dikirim' || suratData.status === 'Terkirim'">
            <span class="info-label">Waktu Ditandatangani</span>
            <span class="info-value">{{ getWaktuDitandatangani() }}</span>
          </div>
          <!-- Waktu Dikirim - Show when status is Terkirim -->
          <div class="info-row" v-if="suratData.status === 'Terkirim'">
            <span class="info-label">Waktu Dikirim</span>
            <span class="info-value">{{ suratData.waktuDikirim ? formatDateTime(suratData.waktuDikirim) : formatDateTime(suratData.updatedAt) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Jenis Surat</span>
            <span class="info-value">{{ suratData.jenisSurat || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Nomor Surat</span>
            <span class="info-value">{{ suratData.nomorSurat || '-' }}</span>
          </div>
          <!-- Perihal Field -->
          <div class="info-row">
            <span class="info-label">Perihal</span>
            <span class="info-value">{{ getPerihal() }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Sifat Surat</span>
            <span class="info-value">{{ suratData.sifatSurat || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Bentuk Surat</span>
            <span class="info-value">{{ getBentukSuratDescription(suratData.bentukSurat) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Penandatangan 1</span>
            <span class="info-value">{{ formatPenandatangan(suratData.penandatanganKiri) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Penandatangan 2</span>
            <span class="info-value">{{ formatPenandatangan(suratData.penandatanganKanan) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Verifikator</span>
            <span class="info-value" v-html="formatVerifikatorList(suratData.verifikator, suratData.status)"></span>
          </div>
          <div class="info-row" v-if="suratData.suratUndanganAcara">
            <span class="info-label">Surat Undangan Acara</span>
            <div class="surat-masuk-container">
              <PaperClipOutlined class="surat-masuk-icon" />

              <a
                href="#"
                @click.stop.prevent="openSuratMasukNewTab"
                class="surat-masuk-name"
              >
                {{ getSuratMasukFileName(suratData.suratUndanganAcara.pathToFile) }}
              </a>

              <div class="surat-masuk-actions">
                <span
                  @click.stop.prevent="openSuratMasukPreview"
                  class="action-icon"
                  title="Preview Surat"
                >
                  <EyeOutlined />
                </span>
                <span
                  @click.stop.prevent="openSuratMasukNewTab"
                  class="action-icon"
                  title="Buka di Tab Baru"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <!-- Bukti Pengiriman - Show for each non-pengurus with bukti (matching detail-hard-copy.vue style) -->
          <template v-for="perwakilan in nonPengurusWithBukti" :key="perwakilan.id">
            <div class="info-row">
              <span class="info-label">
                Bukti Pengiriman
                <div style="font-size: 12px; color: #bfbfbf; font-weight: 400; margin-top: 2px;">
                  {{ perwakilan.namaNonPengurus || perwakilan.user?.name || 'Perwakilan' }}
                </div>
              </span>

              <div class="surat-masuk-container" style="flex-direction: column; align-items: flex-start; gap: 8px;">
                <template v-for="(buktiFile, index) in perwakilan.buktiPengiriman" :key="index">
                  <div style="display: flex; align-items: center; width: 100%;">
                    <PaperClipOutlined class="surat-masuk-icon" />

                    <a
                      :href="getFileUrl(buktiFile)"
                      target="_blank"
                      class="surat-masuk-name"
                      :title="getBuktiFileName(buktiFile, index)"
                    >
                      {{ getBuktiFileName(buktiFile, index) }}
                    </a>

                    <div class="surat-masuk-actions">
                      <span
                        @click.stop.prevent="() => window.open(getFileUrl(buktiFile), '_blank')"
                        class="action-icon"
                        title="Preview Bukti"
                      >
                        <EyeOutlined />
                      </span>

                      <span
                        @click.stop.prevent="() => window.open(getFileUrl(buktiFile), '_blank')"
                        class="action-icon"
                        title="Buka di Tab Baru"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
          <!-- Bukti Pengiriman for Surat Lainnya (surat-level) -->
          <div class="info-row" v-if="!isSuratTugas && suratData.status === 'Terkirim' && hasSuratLainnyaBukti">
            <span class="info-label">Bukti Pengiriman</span>

            <div class="surat-masuk-container" style="flex-direction: column; align-items: flex-start; gap: 8px;">
              <template v-for="(buktiFile, index) in suratLainnyaBuktiList" :key="index">
                <div style="display: flex; align-items: center; width: 100%;">
                  <PaperClipOutlined class="surat-masuk-icon" />

                  <a
                    :href="getFileUrl(buktiFile)"
                    target="_blank"
                    class="surat-masuk-name"
                    :title="getBuktiFileName(buktiFile, index)"
                  >
                    {{ getBuktiFileName(buktiFile, index) }}
                  </a>

                  <div class="surat-masuk-actions">
                    <span
                      @click.stop.prevent="() => window.open(getFileUrl(buktiFile), '_blank')"
                      class="action-icon"
                      title="Preview Bukti"
                    >
                      <EyeOutlined />
                    </span>

                    <span
                      @click.stop.prevent="() => window.open(getFileUrl(buktiFile), '_blank')"
                      class="action-icon"
                      title="Buka di Tab Baru"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Surat Tugas / Dokumen Surat Section -->
        <div class="surat-section">
          <h3 class="section-title">{{ isSuratTugas ? 'Surat Tugas' : 'Dokumen Surat' }}</h3>

          <!-- Upload File Method: Show uploaded files -->
          <template v-if="isUploadFileMethod && uploadedFileList.length > 0">
            <div class="uploaded-files-container">
              <div
                v-for="(file, index) in uploadedFileList"
                :key="index"
                class="file-item"
              >
                <PaperClipOutlined class="file-icon" />
                <a :href="file.url" target="_blank" class="file-link">
                  {{ file.name }}
                </a>
                <a-button type="text" size="small" @click="() => window.open(file.url, '_blank')">
                  <EyeOutlined />
                </a-button>
              </div>
            </div>
          </template>

          <!-- Buat di Sistem Method: Show generated PDF or Surat Tugas -->
          <SuratTugasViewer
            v-else
            :surat-tugas-list="suratTugasList"
            :single-pdf-path="(!isSuratTugas || suratTugasList.length === 0) ? suratData.pdfPath : ''"
            :nomor-surat="suratData.nomorSurat"
            :empty-message="isSuratTugas ? 'Belum ada surat tugas yang dibuat' : 'PDF surat belum tersedia'"
          />
        </div>

        <!-- Mark as Sent Button (only for owner with Belum Dikirim status) -->
        <div class="mark-sent-section" v-if="canMarkSent">
          <a-button
            type="primary"
            class="btn-pill-red"
            @click="handleMarkAsSent"
            :loading="markSentLoading"
          >
            TANDAI SURAT SEBAGAI TERKIRIM
          </a-button>
        </div>
      </template>

      <!-- Error/Empty State -->
      <template v-else-if="!pageLoading">
        <div class="empty-state">
          <a-result
            status="404"
            title="Surat Tidak Ditemukan"
            sub-title="Surat keluar yang Anda cari tidak ditemukan atau telah dihapus."
          >
            <template #extra>
              <a-button type="primary" @click="router.push('/surat-keluar')">
                Kembali ke Daftar Surat
              </a-button>
            </template>
          </a-result>
        </div>
      </template>

      <!-- Cancel Modal -->
      <a-modal
        v-model:visible="cancelModalVisible"
        :footer="null"
        :mask-closable="false"
        centered
        class="cancel-modal"
      >
        <template #title>
          <strong>Yakin ingin menghentikan proses surat keluar?</strong>
        </template>
        <p class="modal-description">
          Tindakan ini akan membatalkan surat keluar dan proses verifikasi dan/atau penandatanganan surat akan dihentikan.
          Surat yang sudah dibatalkan tidak dapat digunakan kembali.
        </p>
        <a-form layout="vertical">
          <a-form-item label="Alasan Pembatalan:" class="cancel-reason-field" required>
            <a-textarea
              v-model:value="cancelReason"
              placeholder="Masukkan alasan pembatalan surat"
              :rows="3"
            />
          </a-form-item>
        </a-form>
        <div class="modal-buttons">
          <a-button
            type="default"
            class="btn-pill-black"
            @click="cancelModalVisible = false"
          >
            KEMBALI
          </a-button>
          <a-button
            class="btn-pill-red"
            :loading="cancelLoading"
            @click="confirmCancelSurat"
          >
            YA, BATALKAN SURAT
          </a-button>
        </div>
      </a-modal>

      <!-- Surat Masuk Preview Modal -->
      <a-modal
        v-model:visible="suratMasukPreviewVisible"
        :title="getSuratMasukFileName(suratData.suratUndanganAcara?.pathToFile) || 'Surat Masuk'"
        :footer="null"
        width="90%"
        :body-style="{ padding: 0, height: '80vh' }"
        centered
        destroy-on-close
      >
        <iframe
          v-if="suratMasukPreviewUrl"
          :src="suratMasukPreviewUrl"
          class="modal-pdf-iframe"
          frameborder="0"
        />
      </a-modal>

      <!-- PDF Preview Modal (for signed documents) -->
      <a-modal
        v-model:visible="pdfPreviewVisible"
        :title="pdfPreviewTitle"
        :footer="null"
        width="90%"
        :body-style="{ padding: 0, height: '80vh' }"
        centered
        destroy-on-close
      >
        <iframe
          v-if="pdfPreviewUrl"
          :src="pdfPreviewUrl"
          class="modal-pdf-iframe"
          frameborder="0"
        />
      </a-modal>

      <!-- Bukti Pengiriman Modal for Soft Copy Non-Pengurus (Surat Tugas) -->
      <a-modal
        v-model:visible="buktiPengirimanModalVisible"
        :footer="null"
        :mask-closable="false"
        :width="700"
        centered
        class="bukti-pengiriman-modal"
      >
        <template #title>
          <strong>Upload Bukti Pengiriman</strong>
        </template>
        <p class="modal-description">
          Mohon upload bukti pengiriman untuk setiap perwakilan dinas sebelum menandai surat sebagai terkirim.
        </p>

        <div class="perwakilan-upload-list">
          <div
            v-for="perwakilan in nonPengurusList"
            :key="perwakilan.id"
            class="perwakilan-upload-item"
          >
            <h4 class="perwakilan-name">
              Bukti Pengiriman - {{ perwakilan.namaNonPengurus || perwakilan.user?.name || 'Perwakilan' }}
            </h4>

            <a-upload
              :file-list="perwakilanBuktiFiles[perwakilan.id] || []"
              :before-upload="() => false"
              @change="(info) => handleBuktiFileChange(perwakilan.id, info)"
              @remove="(file) => handleBuktiFileRemove(perwakilan.id, file)"
              accept=".pdf,.jpg,.jpeg,.png"
              :multiple="true"
              list-type="text"
            >
              <a-button type="default" class="btn-pill-red">
                <template #icon>
                  <PlusOutlined />
                </template>
                Pilih File
              </a-button>
            </a-upload>
          </div>
        </div>

        <div class="modal-buttons">
          <a-button
            class="btn-pill-black"
            @click="buktiPengirimanModalVisible = false"
            :disabled="buktiPengirimanUploading"
          >
            BATAL
          </a-button>
          <a-button
            class="btn-pill-red"
            :loading="buktiPengirimanUploading"
            @click="handleSubmitAllBuktiPengiriman"
          >
            UPLOAD & TANDAI TERKIRIM
          </a-button>
        </div>
      </a-modal>

      <!-- Bukti Pengiriman Modal for Surat Lainnya (surat-level) -->
      <a-modal
        v-model:visible="suratLainnyaBuktiModalVisible"
        :footer="null"
        :mask-closable="false"
        :width="600"
        centered
        class="bukti-pengiriman-modal"
      >
        <template #title>
          <strong>Upload Bukti Pengiriman</strong>
        </template>
        <p class="modal-description">
          Mohon upload bukti pengiriman untuk surat ini sebelum menandai sebagai terkirim.
        </p>

        <div class="upload-section">
          <a-upload
            v-model:file-list="suratLainnyaBuktiFiles"
            :before-upload="() => false"
            @change="handleSuratLainnyaBuktiChange"
            @remove="handleSuratLainnyaBuktiRemove"
            accept=".pdf,.jpg,.jpeg,.png"
            :multiple="true"
            list-type="text"
          >
            <a-button type="default" class="upload-button">
              <template #icon>
                <PlusOutlined />
              </template>
              Pilih File
            </a-button>
          </a-upload>
          <p class="upload-hint">Format: PDF, JPG, PNG (Maks 10MB per file)</p>
        </div>

        <div class="modal-buttons">
          <a-button
            type="default"
            class="btn-pill-black"
            @click="suratLainnyaBuktiModalVisible = false"
            :disabled="suratLainnyaBuktiUploading"
          >
            BATAL
          </a-button>
          <a-button
            type="primary"
            class="btn-pill-red"
            :loading="suratLainnyaBuktiUploading"
            @click="handleSubmitSuratLainnyaBukti"
          >
            UPLOAD & TANDAI TERKIRIM
          </a-button>
        </div>
      </a-modal>
    </div>
  </template>

  <script>
  import { defineComponent, ref, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useStore } from 'vuex'
  import { notification } from 'ant-design-vue'
  import {
    DownloadOutlined,
    StopOutlined,
    CopyOutlined,
    PaperClipOutlined,
    EyeOutlined,
    PlusOutlined,
  } from '@ant-design/icons-vue'
  import { suratKeluarAPI, getFileUrl } from '@/services/axios/perki'
  import { SuratTugasViewer, DetailPembatalan } from '@/components/SuratKeluar'
  import moment from 'moment'
  import 'moment/locale/id'

  moment.locale('id')

  export default defineComponent({
    name: 'SuratKeluarView',
    components: {
      DownloadOutlined,
      StopOutlined,
      CopyOutlined,
      PaperClipOutlined,
      EyeOutlined,
      PlusOutlined,
      SuratTugasViewer,
      DetailPembatalan,
    },
    setup() {
      const router = useRouter()
      const route = useRoute()
      const store = useStore()

      const pageLoading = ref(true)
      const cancelLoading = ref(false)
      const markSentLoading = ref(false)
      const suratData = ref({})
      const suratTugasList = ref([])

      // Get current user info for access control
      const currentUser = JSON.parse(localStorage.getItem('perki_user') || '{}')
      const currentUserId = currentUser.id
      const currentUserRole = localStorage.getItem('perki_role') || currentUser.role

      // Cancel modal
      const cancelModalVisible = ref(false)
      const cancelReason = ref('')

      // Check if current user is the owner (pengaju surat)
      const isOwner = computed(() => {
        return suratData.value.creator?.id === currentUserId
      })

      // Check if user can perform actions (only sekretaris who owns it, or super_admin)
      const canPerformActions = computed(() => {
        // Only super_admin has full access
        if (currentUserRole === 'super_admin') {
          return true
        }
        // Sekretaris can only perform actions on their own surat
        return currentUserRole === 'sekretaris' && isOwner.value
      })

      // Can cancel - only owner sekretaris can cancel, and only for Belum Dikirim status
      const canCancel = computed(() => {
        return suratData.value.status === 'Belum Dikirim' && canPerformActions.value
      })

      // Can mark as sent - only owner sekretaris can mark as sent, and only for Belum Dikirim status
      const canMarkSent = computed(() => {
        return suratData.value.status === 'Belum Dikirim' && canPerformActions.value
      })

      // Can duplicate surat - all sekretaris and super_admin can duplicate
      const canDuplicateSurat = computed(() => {
        return currentUserRole === 'super_admin' || currentUserRole === 'sekretaris'
      })

      // Surat masuk preview
      const suratMasukPreviewVisible = ref(false)
      const suratMasukPreviewUrl = ref('')

      // PDF preview state (for signed documents)
      const pdfPreviewVisible = ref(false)
      const pdfPreviewUrl = ref('')
      const pdfPreviewTitle = ref('')

      // Bukti pengiriman modal for soft copy non-pengurus (Surat Tugas)
      const buktiPengirimanModalVisible = ref(false)
      const buktiPengirimanUploading = ref(false)
      const nonPengurusList = ref([])
      const perwakilanBuktiFiles = ref({}) // { perwakilanId: [files] }

      // Bukti pengiriman modal for Surat Lainnya (surat-level)
      const suratLainnyaBuktiModalVisible = ref(false)
      const suratLainnyaBuktiUploading = ref(false)
      const suratLainnyaBuktiFiles = ref([])

      // Check if this is Surat Tugas type
      const isSuratTugas = computed(() => {
        return suratData.value.jenisSurat === 'Surat Tugas'
      })

      // Check if surat has non-pengurus (for soft copy)
      const hasNonPengurus = computed(() => {
        return suratData.value.perwakilanDinas?.some(p => p.isNonPengurus) || false
      })

      // Check if this is soft copy
      const isSoftCopy = computed(() => {
        return suratData.value.bentukSurat === 'Soft copy'
      })

      // Check if surat uses "Upload File" method
      const isUploadFileMethod = computed(() => {
        return suratData.value.metodePembuatan === 'Upload File'
      })

      // Get uploaded file paths from archivedPdfPaths
      const uploadedFileList = computed(() => {
        if (!isUploadFileMethod.value || !suratData.value.archivedPdfPaths) {
          return []
        }
        try {
          const paths = JSON.parse(suratData.value.archivedPdfPaths)
          if (Array.isArray(paths) && paths.length > 0) {
            return paths.map((path, index) => ({
              path: path,
              name: getFileNameFromPath(path) || `Dokumen ${index + 1}`,
              url: getFileUrl(path),
            }))
          }
        } catch (e) {
          console.error('Failed to parse archivedPdfPaths:', e)
        }
        return []
      })

      // Helper to extract filename from path
      const getFileNameFromPath = (path) => {
        if (!path) return ''
        const parts = path.split('/')
        return parts[parts.length - 1]
      }

      // Check if surat has signed documents (for soft copy display)
      const hasSignedDocuments = computed(() => {
        if (isSuratTugas.value) {
          return suratTugasList.value.length > 0
        }
        return !!suratData.value.pdfPath
      })

      // Check if non-pengurus have bukti pengiriman
      const hasBuktiPengiriman = computed(() => {
        return nonPengurusWithBukti.value.length > 0
      })

      // Get non-pengurus with bukti pengiriman
      const nonPengurusWithBukti = computed(() => {
        if (!suratData.value.perwakilanDinas) return []
        return suratData.value.perwakilanDinas
          .filter(p => p.isNonPengurus && p.buktiPengiriman)
          .map(p => {
            // Parse buktiPengiriman - it's a JSON string from backend
            let buktiArray = []
            if (typeof p.buktiPengiriman === 'string') {
              try {
                buktiArray = JSON.parse(p.buktiPengiriman)
              } catch (e) {
                console.error('Failed to parse buktiPengiriman:', e)
                buktiArray = []
              }
            } else if (Array.isArray(p.buktiPengiriman)) {
              buktiArray = p.buktiPengiriman
            }

            return {
              ...p,
              buktiPengiriman: buktiArray,
            }
          })
          .filter(p => p.buktiPengiriman.length > 0)
      })

      // Check if Surat Lainnya has bukti pengiriman (surat-level)
      const hasSuratLainnyaBukti = computed(() => {
        return suratLainnyaBuktiList.value.length > 0
      })

      // Get Surat Lainnya bukti list (surat-level)
      const suratLainnyaBuktiList = computed(() => {
        if (!suratData.value.buktiPengiriman) return []

        // Parse buktiPengiriman - it's a JSON string from backend
        let buktiArray = []
        if (typeof suratData.value.buktiPengiriman === 'string') {
          try {
            buktiArray = JSON.parse(suratData.value.buktiPengiriman)
          } catch (e) {
            console.error('Failed to parse surat buktiPengiriman:', e)
            buktiArray = []
          }
        } else if (Array.isArray(suratData.value.buktiPengiriman)) {
          buktiArray = suratData.value.buktiPengiriman
        }

        return buktiArray
      })

      // Get status color (for a-tag color prop)
      const getStatusColor = (status) => {
        return store.getters['suratKeluar/getStatusColor'](status)
      }

      // Get status style (custom background and text colors)
      const getStatusStyle = (status) => {
        return store.getters['suratKeluar/getStatusStyle'](status)
      }

      // Format datetime
      const formatDateTime = (dateStr) => {
        if (!dateStr) return '-'
        return moment(dateStr).format('dddd YYYY-MM-DD HH:mm')
      }

      // Get bentuk surat description
      const getBentukSuratDescription = (bentuk) => {
        if (bentuk === 'Soft copy') {
          return 'Soft copy (Menggunakan tanda tangan dan stempel digital. Surat dapat diunduh di website ini (format PDF).)'
        }
        return bentuk || '-'
      }

      // Get perihal - uses namaKegiatan field
      const getPerihal = () => {
        return suratData.value.namaKegiatan || suratData.value.perihal || '-'
      }

      // Format penandatangan
      const formatPenandatangan = (penandatangan) => {
        if (!penandatangan) return 'Tidak ada'
        const name = penandatangan.name || 'Unknown'
        const jabatan = penandatangan.jabatan || ''
        return jabatan ? `${name}, ${jabatan}` : name
      }

      // Format verifikator list with explicit (Setuju) status
      const formatVerifikatorList = verifikator => {
        if (!verifikator || verifikator.length === 0) return '-'

        // Sort by urutan first (ascending)
        const sortedVerifikator = [...verifikator].sort((a, b) => {
          const urutanA = a.urutan || 0
          const urutanB = b.urutan || 0
          return urutanA - urutanB
        })

        return sortedVerifikator
          .map((v, index) => {
            const name = v.user?.name || 'Unknown'
            let statusText = ''

            if (v.status === 'approved') {
              statusText = '(Setuju)'
            } else if (v.status === 'revised' || v.status === 'revision_requested') {
              statusText = '(Revisi)'
            } else if (v.status === 'pending') {
              // Check if all previous verifikators are approved
              const allPreviousApproved = sortedVerifikator
                .slice(0, index)
                .every(prev => prev.status === 'approved')

              if (allPreviousApproved) {
                statusText = '(Sedang Verifikasi)'
              } else {
                statusText = '(Menunggu)'
              }
            }
            return `${name} ${statusText}`
          })
          .join(' → ')
      }

      // Get waktu ditandatangani from penandatangan
      const getWaktuDitandatangani = () => {
        if (!suratData.value.penandatangan || suratData.value.penandatangan.length === 0) {
          return '-'
        }
        // Get the latest signed_at
        const signedAt = suratData.value.penandatangan
          .filter(p => p.signedAt)
          .map(p => moment(p.signedAt))
          .sort((a, b) => b - a)[0]
        return signedAt ? signedAt.format('dddd YYYY-MM-DD HH:mm') : '-'
      }

      // Fetch surat detail
      const fetchData = async () => {
        pageLoading.value = true
        try {
          const id = route.params.id
          const response = await suratKeluarAPI.getById(id)

          if (response.data?.data) {
            suratData.value = response.data.data

            // Build surat tugas list from perwakilan dinas
            if (suratData.value.perwakilanDinas && suratData.value.perwakilanDinas.length > 0) {
              suratTugasList.value = suratData.value.perwakilanDinas
                .filter(p => p.pdfPath)
                .map(p => ({
                  id: p.id,
                  pdfPath: p.pdfPath,
                  perwakilan: p,
                  fileName: `Surat Tugas ${suratData.value.nomorSurat}`,
                }))
            }
          }
        } catch (error) {
          console.error('Error fetching surat detail:', error)
          notification.error({ message: 'Gagal memuat data surat' })
        } finally {
          pageLoading.value = false
        }
      }

      // Handle download surat
      // Handle download surat
      const handleDownloadSurat = () => {
        if (isSuratTugas.value) {
          // For Surat Tugas, download all PDFs
          // FIX: Added 'index' as the second argument here
          suratTugasList.value.forEach((surat, index) => {
            setTimeout(() => {
              downloadFile(surat.pdfPath, surat.fileName || `Surat_Tugas_${index + 1}.pdf`)
            }, index * 100)
          })
        } else {
          // For Surat Lainnya, download single PDF
          if (suratData.value.pdfPath) {
            const fileName = `${suratData.value.nomorSurat || 'Surat'}.pdf`
            downloadFile(suratData.value.pdfPath, fileName)
          } else {
            notification.warning({ message: 'PDF surat tidak tersedia' })
          }
        }
      }

      // Helper function to download file
      // Helper function to download file using Blob method
    const downloadFile = async (filePath, fileName) => {
      const fileUrl = getFileUrl(filePath)

      try {
        // 1. Fetch the file data
        const response = await fetch(fileUrl)

        // 2. Check if the fetch was successful
        if (!response.ok) throw new Error('Network response was not ok')

        // 3. Convert to Blob (Binary Large Object)
        const blob = await response.blob()

        // 4. Create a temporary URL for the Blob
        const blobUrl = window.URL.createObjectURL(blob)

        // 5. Create link and trigger download
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName || 'download.pdf' // This will now strictly work
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // 6. Clean up the URL object to free memory
        window.URL.revokeObjectURL(blobUrl)

      } catch (error) {
        console.error('Download failed, falling back to new tab:', error)
        // Fallback: If fetch fails (e.g. strict CORS), open in new tab
        window.open(fileUrl, '_blank')
      }
    }

      // Handle cancel surat
      const handleCancelSurat = () => {
        cancelReason.value = ''
        cancelModalVisible.value = true
      }

      // Confirm cancel surat
      const confirmCancelSurat = async () => {
        // Validate cancel reason
        if (!cancelReason.value?.trim()) {
          notification.warning({
            message: 'Alasan pembatalan harus diisi',
            description: 'Mohon isi alasan pembatalan surat sebelum melanjutkan.',
          })
          return
        }

        cancelLoading.value = true
        try {
          const id = route.params.id
          const result = await store.dispatch('suratKeluar/cancelSuratKeluar', {
            id: id,
            alasan: cancelReason.value || null,
          })

          if (result.status === 'success') {
            cancelModalVisible.value = false
            notification.success({ message: 'Surat keluar berhasil dibatalkan.' })
            router.push('/surat-keluar')
          } else {
            notification.error({ message: result.message || 'Gagal membatalkan surat' })
          }
        } catch (error) {
          console.error('Error canceling surat:', error)
          notification.error({ message: error.response?.data?.message || 'Gagal membatalkan surat' })
        } finally {
          cancelLoading.value = false
        }
      }

      // Handle duplicate surat
      const handleDuplicateSurat = () => {
        const id = route.params.id
        router.push('/surat-keluar/create?duplicateFrom=' + id)
      }

      // Handle mark as sent
      const handleMarkAsSent = async () => {
        // Check if this is Soft Copy Surat Tugas with non-pengurus
        if (isSoftCopy.value && isSuratTugas.value && hasNonPengurus.value) {
          // Show modal to upload bukti pengiriman for each non-pengurus
          openBuktiPengirimanModal()
          return
        }

        // Check if this is Soft Copy Surat Lainnya (non-Surat Tugas)
        if (isSoftCopy.value && !isSuratTugas.value) {
          // Show modal to upload bukti pengiriman for the surat itself
          openSuratLainnyaBuktiModal()
          return
        }

        // Direct mark as sent for other cases (hard copy without non-pengurus)
        markSentLoading.value = true
        try {
          const id = route.params.id
          const response = await suratKeluarAPI.markSent(id)

          if (response.data?.status === 'success') {
            notification.success({ message: 'Surat berhasil ditandai sebagai terkirim' })
            // Refresh data to show updated status
            await fetchData()
          } else {
            notification.error({ message: response.data?.message || 'Gagal menandai surat sebagai terkirim' })
          }
        } catch (error) {
          console.error('Error marking surat as sent:', error)
          notification.error({ message: error.response?.data?.message || 'Gagal menandai surat sebagai terkirim' })
        } finally {
          markSentLoading.value = false
        }
      }

      // Open bukti pengiriman modal for soft copy non-pengurus
      const openBuktiPengirimanModal = () => {
        // Get list of non-pengurus
        nonPengurusList.value = suratData.value.perwakilanDinas?.filter(p => p.isNonPengurus) || []

        // Initialize empty file lists for each non-pengurus
        perwakilanBuktiFiles.value = {}
        nonPengurusList.value.forEach(perwakilan => {
          perwakilanBuktiFiles.value[perwakilan.id] = []
        })

        buktiPengirimanModalVisible.value = true
      }

      // Handle file change for specific perwakilan
      const handleBuktiFileChange = (perwakilanId, info) => {
        perwakilanBuktiFiles.value[perwakilanId] = info.fileList
      }

      // Handle remove file for specific perwakilan
      const handleBuktiFileRemove = (perwakilanId, file) => {
        const files = perwakilanBuktiFiles.value[perwakilanId] || []
        const index = files.indexOf(file)
        if (index > -1) {
          files.splice(index, 1)
        }
      }

      // Submit all bukti pengiriman and mark as sent
      const handleSubmitAllBuktiPengiriman = async () => {
        // Validate that all non-pengurus have files uploaded
        const allHaveFiles = nonPengurusList.value.every(perwakilan => {
          const files = perwakilanBuktiFiles.value[perwakilan.id] || []
          return files.length > 0
        })

        if (!allHaveFiles) {
          notification.warning({
            message: 'Bukti pengiriman belum lengkap',
            description: 'Mohon upload bukti pengiriman untuk semua perwakilan dinas terlebih dahulu',
          })
          return
        }

        buktiPengirimanUploading.value = true
        try {
          const id = route.params.id

          // Upload bukti pengiriman for each non-pengurus
          for (const perwakilan of nonPengurusList.value) {
            const files = perwakilanBuktiFiles.value[perwakilan.id] || []
            if (files.length > 0) {
              const formData = new FormData()
              files.forEach(fileObj => {
                formData.append('bukti_pengiriman', fileObj.originFileObj)
              })

              await suratKeluarAPI.uploadBuktiPengiriman(id, perwakilan.id, formData)
            }
          }

          // Close modal and refresh data
          buktiPengirimanModalVisible.value = false
          notification.success({
            message: 'Bukti pengiriman berhasil diunggah',
            description: 'Surat telah ditandai sebagai terkirim',
          })
          await fetchData()
        } catch (error) {
          console.error('Error uploading bukti pengiriman:', error)
          notification.error({
            message: 'Gagal mengunggah bukti pengiriman',
            description: error.response?.data?.message || 'Terjadi kesalahan saat mengunggah file',
          })
        } finally {
          buktiPengirimanUploading.value = false
        }
      }

      // Open surat masuk preview
      const openSuratMasukPreview = () => {
        if (suratData.value.suratUndanganAcara?.pathToFile) {
          suratMasukPreviewUrl.value = getFileUrl(suratData.value.suratUndanganAcara.pathToFile)
          suratMasukPreviewVisible.value = true
        }
      }

      // Open surat masuk in new tab
      const openSuratMasukNewTab = () => {
        if (suratData.value.suratUndanganAcara?.pathToFile) {
          window.open(getFileUrl(suratData.value.suratUndanganAcara.pathToFile), '_blank')
        }
      }

      // Get surat masuk file name from path
      const getSuratMasukFileName = (pathToFile) => {
        if (!pathToFile) return ''
        const parts = pathToFile.split('/')
        const fileNameWithPrefix = parts[parts.length - 1]
        // Remove number prefix like "2_" from filename
        const withoutPrefix = fileNameWithPrefix.replace(/^\d+_/, '')
        return withoutPrefix
      }

      // Open Surat Tugas preview at specific index
      const openSuratTugasPreviewAtIndex = (index) => {
        if (suratTugasList.value[index]?.pdfPath) {
          pdfPreviewUrl.value = getFileUrl(suratTugasList.value[index].pdfPath)
          pdfPreviewTitle.value = `Surat Tugas ${suratData.value.nomorSurat}${suratTugasList.value.length > 1 ? ` (${index + 1})` : ''}`
          pdfPreviewVisible.value = true
        }
      }

      // Open Surat Lainnya preview
      const openSuratLainnyaPreview = () => {
        if (suratData.value.pdfPath) {
          pdfPreviewUrl.value = getFileUrl(suratData.value.pdfPath)
          pdfPreviewTitle.value = suratData.value.nomorSurat || 'Dokumen Surat'
          pdfPreviewVisible.value = true
        }
      }

      // Get bukti file name from path
      const getBuktiFileName = (filePath, index) => {
        if (!filePath) return `Bukti Pengiriman ${index + 1}`
        const parts = filePath.split('/')
        const fileName = parts[parts.length - 1]
        return fileName || `Bukti Pengiriman ${index + 1}`
      }

      // Open bukti preview
      const openBuktiPreview = (filePath, perwakilanName, index) => {
        pdfPreviewUrl.value = getFileUrl(filePath)
        pdfPreviewTitle.value = `Bukti Pengiriman - ${perwakilanName}${index !== undefined ? ` (${index + 1})` : ''}`
        pdfPreviewVisible.value = true
      }

      // Open Surat Lainnya bukti modal
      const openSuratLainnyaBuktiModal = () => {
        suratLainnyaBuktiFiles.value = []
        suratLainnyaBuktiModalVisible.value = true
      }

      // Handle Surat Lainnya bukti file change
      const handleSuratLainnyaBuktiChange = (info) => {
        suratLainnyaBuktiFiles.value = info.fileList
      }

      // Handle Surat Lainnya bukti file remove
      const handleSuratLainnyaBuktiRemove = (file) => {
        const index = suratLainnyaBuktiFiles.value.indexOf(file)
        if (index > -1) {
          suratLainnyaBuktiFiles.value.splice(index, 1)
        }
      }

      // Submit Surat Lainnya bukti pengiriman
      const handleSubmitSuratLainnyaBukti = async () => {
        // Validate that files are uploaded
        if (suratLainnyaBuktiFiles.value.length === 0) {
          notification.warning({
            message: 'Bukti pengiriman belum diunggah',
            description: 'Mohon upload minimal 1 file bukti pengiriman',
          })
          return
        }

        suratLainnyaBuktiUploading.value = true
        try {
          const id = route.params.id
          const formData = new FormData()

          suratLainnyaBuktiFiles.value.forEach(fileObj => {
            formData.append('bukti_pengiriman', fileObj.originFileObj)
          })

          // Call the surat-level bukti pengiriman API
          const response = await suratKeluarAPI.uploadBuktiPengirimanSurat(id, formData)

          if (response.data?.status === 'success') {
            suratLainnyaBuktiModalVisible.value = false
            notification.success({
              message: 'Bukti pengiriman berhasil diunggah',
              description: 'Surat telah ditandai sebagai terkirim',
            })
            await fetchData()
          } else {
            notification.error({
              message: response.data?.message || 'Gagal mengunggah bukti pengiriman',
            })
          }
        } catch (error) {
          console.error('Error uploading surat lainnya bukti:', error)
          notification.error({
            message: 'Gagal mengunggah bukti pengiriman',
            description: error.response?.data?.message || 'Terjadi kesalahan saat mengunggah file',
          })
        } finally {
          suratLainnyaBuktiUploading.value = false
        }
      }

      onMounted(() => {
        fetchData()
      })

      return {
        pageLoading,
        cancelLoading,
        markSentLoading,
        suratData,
        suratTugasList,
        isSuratTugas,
        isSoftCopy,
        isUploadFileMethod,
        uploadedFileList,
        hasNonPengurus,
        hasSignedDocuments,
        hasBuktiPengiriman,
        nonPengurusWithBukti,
        hasSuratLainnyaBukti,
        suratLainnyaBuktiList,
        isOwner,
        canPerformActions,
        canCancel,
        canMarkSent,
        canDuplicateSurat,
        cancelModalVisible,
        cancelReason,
        suratMasukPreviewVisible,
        suratMasukPreviewUrl,
        pdfPreviewVisible,
        pdfPreviewUrl,
        pdfPreviewTitle,
        buktiPengirimanModalVisible,
        buktiPengirimanUploading,
        nonPengurusList,
        perwakilanBuktiFiles,
        suratLainnyaBuktiModalVisible,
        suratLainnyaBuktiUploading,
        suratLainnyaBuktiFiles,
        getStatusColor,
        getStatusStyle,
        formatDateTime,
        getBentukSuratDescription,
        getPerihal,
        formatPenandatangan,
        formatVerifikatorList,
        getWaktuDitandatangani,
        getFileUrl,
        handleDownloadSurat,
        handleCancelSurat,
        confirmCancelSurat,
        handleDuplicateSurat,
        handleMarkAsSent,
        openSuratMasukPreview,
        openSuratMasukNewTab,
        getSuratMasukFileName,
        openSuratTugasPreviewAtIndex,
        openSuratLainnyaPreview,
        getBuktiFileName,
        openBuktiPreview,
        handleBuktiFileChange,
        handleBuktiFileRemove,
        handleSubmitAllBuktiPengiriman,
        handleSuratLainnyaBuktiChange,
        handleSuratLainnyaBuktiRemove,
        handleSubmitSuratLainnyaBukti,
        window,
      }
    },
  })
  </script>

  <style lang="scss" scoped>
  .surat-keluar-view {
    padding: 24px;
    background: #FCFCFC;
    min-height: calc(100vh - 64px);
    position: relative;

    @media (max-width: 768px) {
      padding: 16px;
    }

    @media (max-width: 576px) {
      padding: 12px;
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .page-header {
    margin-bottom: 24px;

    @media (max-width: 768px) {
      margin-bottom: 16px;
    }

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #262626;
      margin: 0 0 4px 0;

      @media (max-width: 576px) {
        font-size: 20px;
      }
    }

    .page-subtitle {
      font-size: 16px;
      color: #595959;
      margin: 0 0 8px 0;

      @media (max-width: 576px) {
        font-size: 14px;
      }
    }

    .status-tag {
      border-radius: 50px !important;
      padding: 4px 12px !important;
      font-size: 12px !important;
      font-weight: 700 !important;
    }
  }



  .action-buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap; /* Allows wrapping on small screens */

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 8px;
      margin-bottom: 16px;
    }

    /* 1. Black Outline Pill (For Batalkan, Duplicate, Draft) */
    .btn-pill-black {
      border-radius: 50px;
      font-size: 14px;
      font-weight: 700;
      border: 2px solid #262626; /* Using standard dark grey/black */
      background: white;
      color: #262626;
      padding: 6px 16px; /* Adjusted padding for better visual balance */
      min-height: auto;
      height: auto;
      line-height: 1.5;
      transition: all 0.3s;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px; /* Space between icon and text */
      box-shadow: none;

      @media (max-width: 768px) {
        width: 100%;
        justify-content: center;
      }

      &:hover:not(:disabled) {
        background: #262626 !important;
        border-color: #262626 !important;
        color: white !important;
      }

      &:disabled {
        border-color: #d9d9d9;
        background: #f5f5f5;
        color: #d9d9d9;
      }
    }

    /* 2. Red Outline Pill (For Simpan/Download) */
    .btn-pill-red {
      border-radius: 50px;
      font-size: 14px;
      font-weight: 700;
      border: 2px solid #e10f28; /* Your app's red color */
      background: white;
      color: white;
      padding: 6px 16px;
      min-height: auto;

      @media (max-width: 768px) {
        width: 100%;
        justify-content: center;
      }
      height: auto;
      line-height: 1.5;
      transition: all 0.3s;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      box-shadow: none;

      &:hover:not(:disabled) {
        background: white !important;
        border-color: #e10f28 !important;
        color: #e10f28 !important;
      }

      &:disabled {
        border-color: #d9d9d9;
        background: #f5f5f5;
        color: #d9d9d9;
      }
    }

    .submit-section {
      display: flex;
      gap: 16px;
      padding-top: 24px;
      border-top: 1px solid #f0f0f0;
      align-items: center;
      flex-wrap: wrap;
    }
  }

  /* B. OUTLINE RED PILL (For: Tambah Foto, Upload) */
.btn-pill-red-outline {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  background: white;
  color: #e10f28;
  padding: 6px 20px;
  min-height: auto;
  height: 42px;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  /* Force Icon Color Red */
  :deep(.anticon) {
    color: #e10f28;
  }

  &:hover:not(:disabled) {
    background: #e10f28 !important; /* Light Pink */
    border-color: #e10f28 !important;
    color: white !important;

    :deep(.anticon) {
      color: white;
    }
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }
}

  .info-card {
    background: white;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

    @media (max-width: 768px) {
      padding: 16px;
      margin-bottom: 16px;
    }

    @media (max-width: 576px) {
      padding: 12px;
    }

    .info-row {
      display: flex;
      padding: 12px 0;
      border-bottom: 1px solid #f0f0f0;
      align-items: center; /* Ensures vertical alignment */

      @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        padding: 8px 0;
      }

      &:last-child {
        border-bottom: none;
      }

      .info-label {
        width: 200px;
        color: #8c8c8c; /* Gray color for label */
        font-weight: 500;
        flex-shrink: 0;

        @media (max-width: 768px) {
          width: 100%;
          margin-bottom: 4px;
          font-size: 13px;
        }
      }

      .info-value {
        color: #262626;
        flex: 1;

        @media (max-width: 768px) {
          width: 100%;
          font-size: 14px;
        }
      }

      /* Styling for Surat Undangan Acara content */
      .surat-masuk-container {
        display: flex;
        align-items: center;
        flex: 1;
        gap: 8px;
        /* Ensure container takes available width but allows content to shrink */
        min-width: 0;

        @media (max-width: 576px) {
          width: 100%;
        }

        .signed-doc-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          min-width: 0;
          padding: 4px 0;
        }

        .bukti-pengiriman-group {
          width: 100%;
          padding: 12px;
          background: #fafafa;
          border-radius: 6px;
          border: 1px solid #f0f0f0;

          .perwakilan-info {
            margin-bottom: 8px;
            font-size: 14px;
            color: #262626;

            strong {
              font-weight: 600;
            }

            .jabatan-text {
              color: #8c8c8c;
              font-size: 13px;
            }
          }

          .bukti-files-list {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
        }

        .surat-masuk-icon {
          color: #8c8c8c; /* Gray paperclip */
          font-size: 16px;
          flex-shrink: 0; /* Prevent icon from shrinking */
        }

        .surat-masuk-name {
          color: #321e8d; /* Purple/Blue link color */
          font-size: 14px;
          text-decoration: none;

          /* Text truncation logic */
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1; /* Take up remaining space */
          min-width: 0; /* Critical for flex child truncation */
          display: block; /* Required for text-overflow to work in some flex contexts */

          &:hover {
            text-decoration: underline;
          }
        }

        .surat-masuk-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: 4px; /* Reduced margin */
          flex-shrink: 0; /* Prevent actions from shrinking */

          .action-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            color: #e01f48; /* Red color for icons */
            cursor: pointer;
            transition: all 0.2s;
            border-radius: 4px;

            &:hover {
              background-color: #fff1f0;
            }

            svg {
              width: 14px;
              height: 14px;
            }
          }
        }
      }
    }
  }

  .surat-section {
    background: white;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #262626;
      margin: 0 0 16px 0;
    }

    .uploaded-files-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .file-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      background: #f5f5f5;
      border-radius: 4px;
      transition: background 0.2s;

      &:hover {
        background: #e8e8e8;
      }
    }

    .file-icon {
      color: #8c8c8c;
      font-size: 16px;
    }

    .file-link {
      flex: 1;
      color: #262626;
      text-decoration: none;

      &:hover {
        color: #E10F28;
      }
    }
  }

  .mark-sent-section {
    margin-top: 24px;
    .btn-pill-red {
      border-radius: 50px;
      font-size: 14px;
      font-weight: 700;
      border: 2px solid #E10F28; /* Your app's red color */
      background: #E10F28;
      color: white;
      padding: 6px 16px;
      min-height: auto;
      height: auto;
      line-height: 1.5;
      transition: all 0.3s;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      box-shadow: none;

      &:hover:not(:disabled) {
        background: white !important;
        border-color: #E10F28 !important;
        color: #E10F28 !important;
      }

      &:disabled {
        border-color: #d9d9d9;
        background: #f5f5f5;
        color: #d9d9d9;
      }
    }
  }

  .modal-pdf-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
  </style>

  <style lang="scss">
  // Bukti Pengiriman Modal Styles (unscoped for modal portal)
  .bukti-pengiriman-modal {
    .modal-description {
      color: #595959;
      font-size: 14px;
      line-height: 1.6;
      margin-bottom: 24px;
    }

    .upload-section {
      margin-bottom: 24px;

      .upload-button {
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
      }

      .upload-hint {
        font-size: 12px;
        color: #8c8c8c;
        margin: 8px 0 0 0;
      }
    }

    .perwakilan-upload-list {
      max-height: 400px;
      overflow-y: auto;
      margin-bottom: 24px;

      .btn-pill-black {
        border-radius: 50px;
        font-size: 14px;
        font-weight: 700;
        border: 2px solid #262626; /* Using standard dark grey/black */
        background: white;
        color: #262626;
        padding: 6px 16px; /* Adjusted padding for better visual balance */
        min-height: auto;
        height: auto;
        line-height: 1.5;
        transition: all 0.3s;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px; /* Space between icon and text */
        box-shadow: none;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: center;
        }

        &:hover:not(:disabled) {
          background: #262626 !important;
          border-color: #262626 !important;
          color: white !important;
        }

        &:disabled {
          border-color: #d9d9d9;
          background: #f5f5f5;
          color: #d9d9d9;
        }
      }

      .btn-pill-red {
        border-radius: 50px;
        font-size: 14px;
        font-weight: 700;
        border: 2px solid #e10f28; /* Your app's red color */
        background: white;
        color: #e10f28;
        padding: 6px 16px;
        min-height: auto;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: center;
        }
        height: auto;
        line-height: 1.5;
        transition: all 0.3s;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        box-shadow: none;

        &:hover:not(:disabled) {
          background: #e10f28 !important;
          border-color: #e10f28 !important;
          color: white !important;
        }

        &:disabled {
          border-color: #d9d9d9;
          background: #f5f5f5;
          color: #d9d9d9;
        }
      }
    }

    .perwakilan-upload-item {
      padding: 16px;
      border: 1px solid #f0f0f0;
      border-radius: 8px;
      margin-bottom: 16px;
      background: #fafafa;

      &:last-child {
        margin-bottom: 0;
      }

      .perwakilan-name {
        font-size: 15px;
        font-weight: 600;
        color: #262626;
        margin: 0 0 4px 0;
      }

      .perwakilan-detail {
        font-size: 13px;
        color: #8c8c8c;
        margin: 0 0 12px 0;
      }

      .upload-button {
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
      }

      .upload-hint {
        font-size: 12px;
        color: #8c8c8c;
        margin: 8px 0 0 0;
      }
    }

    .modal-buttons {
      display: flex;
      gap: 16px;
      padding-top: 16px;
      border-top: 1px solid #f0f0f0;

      .btn-pill-black {
        border-radius: 50px;
        font-size: 14px;
        font-weight: 700;
        border: 2px solid #262626; /* Using standard dark grey/black */
        background: white;
        color: #262626;
        padding: 6px 16px; /* Adjusted padding for better visual balance */
        min-height: auto;
        height: auto;
        line-height: 1.5;
        transition: all 0.3s;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px; /* Space between icon and text */
        box-shadow: none;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: center;
        }

        &:hover:not(:disabled) {
          background: #262626 !important;
          border-color: #262626 !important;
          color: white !important;
        }

        &:disabled {
          border-color: #d9d9d9;
          background: #f5f5f5;
          color: #d9d9d9;
        }
      }

      .btn-pill-red {
        border-radius: 50px;
        font-size: 14px;
        font-weight: 700;
        border: 2px solid #e10f28; /* Your app's red color */
        background: white;
        color: #e10f28;
        padding: 6px 16px;
        min-height: auto;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: center;
        }
        height: auto;
        line-height: 1.5;
        transition: all 0.3s;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        box-shadow: none;

        &:hover:not(:disabled) {
          background: #e10f28 !important;
          border-color: #e10f28 !important;
          color: white !important;
        }

        &:disabled {
          border-color: #d9d9d9;
          background: #f5f5f5;
          color: #d9d9d9;
        }
      }

      .btn-pill-black,
      .btn-pill-red {
        flex: 1;
      }
    }
  }

  // Cancel Modal Styles (unscoped for modal portal)
  .cancel-modal {
    .modal-description {
      color: #595959;
      font-size: 14px;
      line-height: 1.6;
      margin-bottom: 24px;
    }

    .cancel-reason-field {
      margin-bottom: 32px;

      .ant-form-item-label > label {
        font-weight: 600;
        color: #262626;
        font-size: 14px;
      }

      .ant-input-textarea {
        textarea {
          border-radius: 8px;
          border: 1px solid #d9d9d9;
          padding: 12px;
          font-size: 14px;

          &:focus {
            border-color: #e10f28;
            box-shadow: 0 0 0 2px rgba(225, 15, 40, 0.1);
          }
        }
      }
    }
    /* --- LOGIC TO MOVE ASTERISK TO RIGHT --- */
    :deep(.ant-form-item) {
      margin-bottom: 16px;

      .ant-form-item-label > label {
        font-weight: 500;
        color: #262626;
        font-size: 14px;

        &.ant-form-item-required {
          /* 1. Hide the default asterisk on the LEFT */
          &::before {
            display: none !important;
          }

          /* 2. Create the new asterisk on the RIGHT */
          &::after {
            display: inline-block;
            margin-left: 4px;
            color: #ff4d4f;
            font-size: 14px;
            font-family: SimSun, sans-serif;
            line-height: 1;
            content: '*';
            visibility: visible !important;
          }
        }
      }
    }

    .modal-buttons {
      display: flex;
      gap: 16px;
      width: 100%;

      .btn-pill-black {
        border-radius: 50px;
        font-size: 14px;
        font-weight: 700;
        border: 2px solid #262626; /* Using standard dark grey/black */
        background: white;
        color: #262626;
        padding: 6px 16px; /* Adjusted padding for better visual balance */
        min-height: auto;
        height: auto;
        line-height: 1.5;
        transition: all 0.3s;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px; /* Space between icon and text */
        box-shadow: none;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: center;
        }

        &:hover:not(:disabled) {
          background: #262626 !important;
          border-color: #262626 !important;
          color: white !important;
        }

        &:disabled {
          border-color: #d9d9d9;
          background: #f5f5f5;
          color: #d9d9d9;
        }
      }

      .btn-pill-red {
        border-radius: 50px;
        font-size: 14px;
        font-weight: 700;
        border: 2px solid #e10f28; /* Your app's red color */
        background: white;
        color: #e10f28;
        padding: 6px 16px;
        min-height: auto;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: center;
        }
        height: auto;
        line-height: 1.5;
        transition: all 0.3s;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        box-shadow: none;

        &:hover:not(:disabled) {
          background: #e10f28 !important;
          border-color: #e10f28 !important;
          color: white !important;
        }

        &:disabled {
          border-color: #d9d9d9;
          background: #f5f5f5;
          color: #d9d9d9;
        }
      }
      /* Force buttons to share width equally (50:50) */
      .btn-pill-black,
      .btn-pill-red {
        flex: 1;
        width: 100%;
        text-align: center;
      }
    }
  }
  </style>
